import { useFrame } from '@react-three/fiber'
import { useMemo, useRef } from 'react'
import * as THREE from 'three'
import type { YogaPose } from '../types'
import { MUSCLE_CATALOG, ROLE_COLORS } from '../muscles/catalog'
import { MUSCLE_PLACEMENT } from '../muscles/placement'
import { useAppStore } from '../store'

interface Props {
  bones: Map<string, THREE.Bone>
  pose: YogaPose
}

interface HighlightSpec {
  key: string
  from: string
  to: string
  t: number
  /** offset in bone-local axes (x, along-segment ignored, z) after side/anatomy mapping */
  local: THREE.Vector3
  color: string
  radius: number
  lengthFrac: number
}

const _a = new THREE.Vector3()
const _b = new THREE.Vector3()
const _dir = new THREE.Vector3()
const _q = new THREE.Quaternion()
const _off = new THREE.Vector3()
const _up = new THREE.Vector3(0, 1, 0)

export function MuscleHighlights({ bones, pose }: Props) {
  const cardsMode = useAppStore((s) => s.cardsMode)
  const phase = useAppStore((s) => s.phase)
  const poseWeight = useAppStore((s) => s.poseWeight)
  const meshes = useRef<THREE.Mesh[]>([])

  const specs = useMemo(() => {
    const list: HighlightSpec[] = []
    for (const muscle of pose.muscles) {
      if (!muscle.hasMesh) continue
      if (cardsMode === 'off') continue
      if (cardsMode === 'working-stabilizing' && muscle.role === 'lengthening') continue
      if (!MUSCLE_CATALOG[muscle.id]) continue
      const color = ROLE_COLORS[muscle.role]
      for (const pl of MUSCLE_PLACEMENT[muscle.id] ?? []) {
        const sides: ('Left' | 'Right' | '')[] = pl.limb === 'axial' ? (pl.pair ? ['Left', 'Right'] : ['']) : ['Left', 'Right']
        if (muscle.side === 'left' && pl.limb !== 'axial') sides.splice(1, 1)
        if (muscle.side === 'right' && pl.limb !== 'axial') sides.splice(0, 1)
        sides.forEach((side) => {
          const ant = pl.ant ?? 0
          const med = pl.med ?? 0
          let local: THREE.Vector3
          let from = `mixamorig${pl.bone}`
          let to = `mixamorig${pl.to}`
          if (pl.limb === 'leg') {
            from = `mixamorig${side}${pl.bone}`; to = `mixamorig${side}${pl.to}`
            local = new THREE.Vector3(side === 'Left' ? med : -med, 0, ant)
          } else if (pl.limb === 'arm') {
            from = `mixamorig${side}${pl.bone}`; to = `mixamorig${side}${pl.to}`
            // upper-arm local X: posterior on the left, anterior on the right; local Z points medial-inferior
            local = new THREE.Vector3(side === 'Left' ? -ant : ant, 0, med)
          } else {
            // axial: local X = figure's left, Z = anterior; `med` = lateral distance for paired capsules
            local = new THREE.Vector3(side === 'Left' ? med : side === 'Right' ? -med : 0, 0, ant)
          }
          list.push({ key: `${muscle.id}-${side}-${pl.bone}`, from, to, t: pl.t, local, color, radius: pl.radius, lengthFrac: pl.length })
        })
      }
    }
    return list
  }, [pose, cardsMode])

  useFrame(() => {
    const intensity =
      phase === 'entry' || phase === 'release' ? poseWeight : Math.max(0.55, poseWeight)
    specs.forEach((spec, i) => {
      const mesh = meshes.current[i]
      const a = bones.get(spec.from)
      const b = bones.get(spec.to)
      if (!mesh || !a || !b) return
      a.getWorldPosition(_a)
      b.getWorldPosition(_b)
      _dir.subVectors(_b, _a)
      const len = _dir.length()
      if (len < 1e-4) return
      _dir.divideScalar(len)
      a.getWorldQuaternion(_q)
      _off.copy(spec.local).applyQuaternion(_q)
      mesh.position.copy(_a).addScaledVector(_dir, len * spec.t).add(_off)
      mesh.quaternion.setFromUnitVectors(_up, _dir)
      const body = Math.max(0.001, len * spec.lengthFrac - spec.radius * 2)
      mesh.scale.set(1, (body + spec.radius * 2) / (0.02 + spec.radius * 2), 1)
      const mat = mesh.material as THREE.MeshStandardMaterial
      mat.opacity = 0.78 * intensity
      mat.emissiveIntensity = 0.55 * intensity
    })
  })

  return (
    <group>
      {specs.map((spec, i) => (
        <mesh
          key={spec.key}
          ref={(el) => { if (el) meshes.current[i] = el }}
          renderOrder={2}
        >
          <capsuleGeometry args={[spec.radius, 0.02, 6, 12]} />
          <meshStandardMaterial
            color={spec.color}
            transparent
            opacity={0.78}
            depthWrite={false}
            emissive={spec.color}
            emissiveIntensity={0.55}
            roughness={0.5}
          />
        </mesh>
      ))}
    </group>
  )
}
